import React, { useEffect, useState } from 'react';
import {
  Collapse,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';
import PropTypes from 'prop-types';

import {
  trackEvent,
  TRACKS_THIRD_ARM,
} from 'src/util/segment-storeless';

import TipBox from 'src/components/TipBox';
import ToggleStep from './ToggleStep';
import styles from './style.module.css';

const ServiceStep = (props) => {
  const {
    firstEscalationPolicyId,
    createService,
    firstService,
    isServiceApiFetching,
    serviceApiError,
    getSegmentProps,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [inputError, setInputError] = useState(null);
  const [shouldAttemptTrack, setShouldAttemptTrack] = useState(false);

  const hasService = Object.keys(firstService).length > 0;
  const toggle = () => {
    if (!isOpen && hasService) {
      trackEvent(
        TRACKS_THIRD_ARM.see_step_again,
        getSegmentProps({
          step_name: 'service_created'
        })
      );
    }
    setIsOpen(!isOpen);
  };

  const isErrorState = Boolean(serviceApiError || inputError);

  const collapseStyles = isOpen ? styles.stepCollapse : null;
  const errorStyles = isErrorState ? styles.errorState : null;

  useEffect(() => {
    if (shouldAttemptTrack && hasService && !isErrorState) {
      trackEvent(
        TRACKS_THIRD_ARM.service_created,
        getSegmentProps({
          service_id: firstService.id,
          service_name: firstService.name
        })
      );

      setShouldAttemptTrack(false);
    }
  }, [shouldAttemptTrack, hasService, isErrorState]);

  const submitNewService = (event) => {
    event.preventDefault();

    const serviceName = event.target.service.value;

    if (!serviceName) {
      setInputError('Please specify a service name.');
    } else if (serviceName.length > 100) {
      setInputError('Please select a service name with a length of 100 characters or fewer.');
    } else {
      setShouldAttemptTrack(true);
      setInputError(null);
      createService(serviceName, firstEscalationPolicyId);
    }
  };

  const ServiceCopy = () => (
    <p data-test="onboarding-service-step-copy">
      A Service is the name of a component of your business that you&apos;d like to monitor
      performance, track incidents on, or go on-call for. Later, you can set up an integration
      between your other tools and PagerDuty to bring event data you&apos;d like to use to
      trigger real-time responses that power your Service.
    </p>
  );

  const ServiceForm = () => (
    <Form
      onSubmit={submitNewService}
      data-test="onboarding-service-step-form"
    >
      <FormGroup>
        <Label
          for="serviceName"
          className="font-weight-bold"
        >
          Let&apos;s give your Service a name!
        </Label>
        <Input
          type="text"
          className={errorStyles}
          name="service"
          id="serviceName"
          placeholder="Shopping Cart, Email Service, API Monitoring, etc..."
          disabled={isServiceApiFetching}
          data-test="onboarding-service-step-input"
        />
      </FormGroup>
      <div className="w-100 text-center">
        <Button
          color="primary"
          size="sm"
          className="px-2 mb-2"
          disabled={isServiceApiFetching}
          data-test="onboarding-service-step-submit"
        >
          Create Your First Service
        </Button>
      </div>
    </Form>
  );

  const ServiceCreatedCopy = () => (
    <div className="my-2 text-center" data-test="onboarding-service-step-exist">
      Your service
      &nbsp;
      <strong>{firstService.name}</strong>
      &nbsp;
      is ready to go!
    </div>
  );

  const ServiceErrorCopy = () => {
    const errorMessage = inputError || serviceApiError.message;
    return (
      <div className={`mb-1 text-center ${errorStyles}`} data-test="onboarding-service-step-error">
        <p>{`There was a problem: ${errorMessage}`}</p>
        <p>Please try your request again.</p>
      </div>
    );
  };

  const ServiceTip = () => (
    <TipBox data-test="onboarding-service-step-tip">
      <strong>Pro Tip: </strong>
      Once you&apos;ve set up your first Service, you can view, make edits,
      and see incidents associated with your Services in the&nbsp;
      <a href="/service-directory">Service Directory.</a>
    </TipBox>
  );

  return (
    <div>
      <ToggleStep
        isOpen={isOpen}
        toggle={toggle}
        stepNumber="1"
        stepTitle="Create your first service"
        isCompleted={hasService}
      />
      <Collapse className={collapseStyles} isOpen={isOpen} data-test="onboarding-service-step-toggle">
        { ServiceCopy() }
        { hasService ? ServiceCreatedCopy() : ServiceForm() }
        { isErrorState && ServiceErrorCopy() }
        { ServiceTip() }
      </Collapse>
    </div>
  );
};

ServiceStep.defaultProps = {
  firstService: {
    id: '',
    name: '',
  },
  serviceApiError: null,
};

ServiceStep.propTypes = {
  firstEscalationPolicyId: PropTypes.string.isRequired,
  createService: PropTypes.func.isRequired,
  firstService: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  isServiceApiFetching: PropTypes.bool.isRequired,
  serviceApiError: PropTypes.instanceOf(Error),
  getSegmentProps: PropTypes.func.isRequired,
};

export default ServiceStep;
